import React from 'react';
import { User, Briefcase, GraduationCap, FileText, Phone, Mail, MapPin, Link as LinkIcon } from 'lucide-react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  ContactInfo,
  ResumeSection,
  SummarySection,
  CoreCompetenciesSection,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  PaperSection,
  SkillSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 6 - 사이드바가 있는 모던한 디자인 (사진 없는 버전)
const Template6 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`template-6 resume-container bg-white flex ${language === 'en' ? 'resume-english' : ''}`}>
      {/* Left Sidebar */}
      <div className="w-1/3 bg-slate-800 text-white p-6">
        {/* Name and Title Section */}
        <div className="mb-8 pb-6 border-b border-slate-600">
          <h1 className={getPlaceholderClass(hasRealData.name, "text-2xl font-bold mb-2 uppercase text-white", "text-2xl font-bold mb-2 uppercase text-slate-400 italic")}>
            {currentData.name}
          </h1>
          <p className={getPlaceholderClass(hasRealData.title, "text-sm uppercase tracking-wider text-slate-300", "text-sm uppercase tracking-wider text-slate-400 italic")}>
            {currentData.title}
          </p>
        </div>

        {/* Contact Section */}
        <div className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-4 text-slate-300">{titles.contact.toUpperCase()}</h2>
          <div className="space-y-2 text-sm">
            {currentData.phone && (
              <div className={`flex items-center gap-2 ${hasRealData.phone ? "text-white" : "text-slate-500 italic"}`}>
                <Phone className="w-4 h-4" />
                <span>{currentData.phone}</span>
              </div>
            )}
            {currentData.email && (
              <div className={`flex items-center gap-2 ${hasRealData.email ? "text-white" : "text-slate-500 italic"}`}>
                <Mail className="w-4 h-4" />
                <span>{currentData.email}</span>
              </div>
            )}
            {currentData.address && (
              <div className={`flex items-center gap-2 ${hasRealData.address ? "text-white" : "text-slate-500 italic"}`}>
                <MapPin className="w-4 h-4" />
                <span>{currentData.address}</span>
              </div>
            )}
            {currentData.socialLinks && currentData.socialLinks.map((social, index) => (
              <div key={index} className={`flex items-center gap-2 ${hasRealData.socialLinks ? "text-white" : "text-slate-500 italic"}`}>
                <LinkIcon className="w-4 h-4" />
                <span>{social.url.replace(/^https?:\/\/(www\.)?/, '')}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Section */}
        <div className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-4 text-slate-300">{titles.skills.toUpperCase()}</h2>
          <div className="space-y-2">
            {currentData.skills.map((skill, index) => (
              <div key={index} className={`text-sm flex items-start ${hasRealData.skills ? "text-white" : "text-slate-500 italic"}`}>
                <span className={hasRealData.skills ? "text-slate-400 mr-2" : "text-slate-600 mr-2"}>•</span>
                <span>{skill}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Languages Section (if language data exists) */}
        {currentData.languages && currentData.languages.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-bold uppercase tracking-wider mb-4 text-slate-300">{titles.languages ? titles.languages.toUpperCase() : 'LANGUAGES'}</h2>
            <div className="space-y-2 text-sm">
              {currentData.languages.map((lang, index) => (
                <div key={index}>
                  <span className={hasRealData.languages ? "font-medium" : "font-medium text-slate-500"}>{lang.language}</span>
                  <span className={hasRealData.languages ? "text-slate-400 ml-2" : "text-slate-600 ml-2"}>({lang.level})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certificates Section in Sidebar */}
        {currentData.certificates.length > 0 && (
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider mb-4 text-slate-300">{titles.certificates}</h2>
            <div className="space-y-3">
              {currentData.certificates.map((cert, index) => (
                <div key={index} className="text-sm">
                  <div className={hasRealData.certificates ? "font-medium text-white" : "font-medium text-slate-500 italic"}>
                    {cert.name}
                  </div>
                  <div className={hasRealData.certificates ? "text-xs text-slate-400" : "text-xs text-slate-600 italic"}>
                    {cert.issuer}
                  </div>
                  <div className={hasRealData.certificates ? "text-xs text-slate-400" : "text-xs text-slate-600 italic"}>
                    {cert.date}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right Main Content */}
      <div className="w-2/3 p-8">
        {/* Profile Section */}
        <ResumeSection
          title={titles.introduction}
          variant="custom"
          sectionClassName="mb-8"
          titleClassName="text-lg font-bold uppercase tracking-wider mb-4 text-gray-800 flex items-center gap-2"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold uppercase tracking-wider text-gray-800">{titles.introduction}</h2>
          </div>
          {currentData.summary && (
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm mb-4 leading-relaxed text-gray-700"
            />
          )}
          {currentData.keyStrengths.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{titles.coreCompetencies}</h3>
              <CoreCompetenciesSection
                keyStrengths={currentData.keyStrengths}
                hasRealData={hasRealData}
                className="text-sm space-y-1"
                bulletStyle="•"
                bulletClassName="text-slate-600"
                textClassName="text-gray-700"
              />
            </div>
          )}
        </ResumeSection>

        {/* Work Experience Section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold uppercase tracking-wider text-gray-800">{titles.experience}</h2>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[-19px] top-0 bottom-0 w-0.5 bg-gray-300"></div>
            
            {currentData.experiences.map((exp, index) => (
              <div key={index} className="mb-6 last:mb-0 relative">
                {/* Timeline dot */}
                <div className="absolute left-[-24px] top-2 w-3 h-3 bg-white rounded-full border-2 border-slate-800"></div>
                
                <ExperienceSection
                  experiences={[exp]}
                  hasRealData={hasRealData}
                  datePosition="right"
                  companyClassName="text-base font-bold text-gray-800"
                  positionClassName="text-sm text-gray-600"
                  dateClassName="text-sm text-gray-500"
                  descriptionClassName="text-sm mt-2"
                  projectTitleClassName="text-sm font-semibold text-gray-700"
                  projectDescriptionClassName="text-sm mt-1"
                  projectIndent="pl-4"
                  itemClassName=""
                />
              </div>
            ))}
          </div>
        </section>

        {/* Education Section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold uppercase tracking-wider text-gray-800">{titles.education}</h2>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[-19px] top-0 bottom-0 w-0.5 bg-gray-300"></div>
            
            {currentData.education.map((edu, index) => (
              <div key={index} className="mb-4 last:mb-0 relative">
                {/* Timeline dot */}
                <div className="absolute left-[-24px] top-2 w-3 h-3 bg-white rounded-full border-2 border-slate-800"></div>
                
                <EducationSection
                  education={[edu]}
                  hasRealData={hasRealData}
                  datePosition="right"
                  degreeClassName="text-base font-bold text-gray-800"
                  schoolClassName="text-sm text-gray-600"
                  majorClassName="text-sm text-gray-500"
                  dateClassName="text-sm text-gray-500"
                  showMajor={true}
                  itemClassName=""
                />
              </div>
            ))}
          </div>
        </section>

        {/* Papers Section */}
        {hasRealData.papers && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-lg font-bold uppercase tracking-wider text-gray-800">{titles.papers}</h2>
            </div>
            <PaperSection
              papers={currentData.papers}
              hasRealData={hasRealData}
              getPaperTypeText={getPaperTypeText}
              titleClassName="text-base font-semibold text-gray-800"
              authorsClassName="text-sm text-gray-600"
              journalClassName="text-sm text-gray-700"
              typeClassName="ml-2 px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded"
            />
          </section>
        )}
      </div>
    </div>
  );
};

export default Template6;